import React, { useEffect, useState } from "react";
import { useProfile } from "../../context/ProfileContext";
import { AutosaveIndicator, Skeleton } from "../../design-system";
import useAutosave from "../../hooks/useAutosave";
import UbicacionSelector from "../../components/UbicacionSelector";
import { humanizeAxiosError } from "../../lib/api";
import { Check, AlertCircle } from "lucide-react";

/**
 * PerfilGlobalPage — editor del Perfil Global.
 *
 * Regla UX crítica (Etapa 3 §6.9): un solo campo activo, auto-guardado on blur.
 * Regla UX (Etapa 2 §2.4): borrador local en teclado, persistencia en blur.
 */

const TIPOS_USUARIO = [
  { value: "cliente", label: "Cliente" },
  { value: "contratista", label: "Maestro-Contratista" },
  { value: "ferreteria", label: "Ferretería" },
];

export default function PerfilGlobalPage() {
  const { perfilGlobal, loading, updateGlobal, error, refresh } = useProfile();
  const [local, setLocal] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const auto = useAutosave(async (patch) => {
    try {
      await updateGlobal(patch);
      setFieldErrors({});
    } catch (e) {
      const msg = humanizeAxiosError(e);
      // Marcamos error genérico si el server no dio detalle por campo.
      setFieldErrors({ _global: msg });
      throw e;
    }
  });

  useEffect(() => {
    if (perfilGlobal && !local) {
      setLocal({
        nombre_completo: perfilGlobal.nombre_completo || "",
        telefono: perfilGlobal.telefono || "",
        descripcion_corta: perfilGlobal.descripcion_corta || "",
        tipo_usuario: perfilGlobal.tipo_usuario || "cliente",
        ubicacion: perfilGlobal.ubicacion || null,
      });
    }
  }, [perfilGlobal, local]);

  if (loading || !local || !perfilGlobal) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-4" data-testid="pg-loading">
        <Skeleton variant="input" count={4} />
      </div>
    );
  }

  const commit = (field, value) => {
    if (value === perfilGlobal[field]) return;
    auto.schedule({ [field]: value });
  };

  const commitUbicacion = (ubic) => {
    if (!ubic || !ubic.region || !ubic.provincia || !ubic.distrito) return;
    // No enviar si es la misma que la persistida.
    const p = perfilGlobal.ubicacion || {};
    if (p.region === ubic.region && p.provincia === ubic.provincia && p.distrito === ubic.distrito) return;
    auto.schedule({ ubicacion: { region: ubic.region, provincia: ubic.provincia, distrito: ubic.distrito } });
  };

  const desc = local.descripcion_corta || "";
  const descLen = desc.length;

  return (
    <div className="space-y-6" data-testid="perfil-global-page">
      {error && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" role="alert" data-testid="pg-load-error">
          <span className="flex items-center gap-2">
            <AlertCircle size={16} className="flex-shrink-0" />
            No pudimos cargar tu perfil completo. Mostramos tus datos básicos mientras tanto.
          </span>
          <button
            type="button"
            onClick={() => refresh()}
            data-testid="pg-retry"
            className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700"
          >
            Reintentar
          </button>
        </div>
      )}
      {/* Card contenedora */}
      <div className="bg-white rounded-2xl border border-slate-100">
        <div className="flex items-center justify-between px-6 pt-6 pb-3 border-b border-slate-100">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Perfil Global</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Tu identidad principal en ConstruLink. Se guarda automáticamente.
            </p>
          </div>
          <AutosaveIndicator state={auto.state} lastSavedAt={auto.lastSavedAt} testid="pg-autosave" />
        </div>

        <div className="p-6 space-y-6">
          {fieldErrors._global && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm" role="alert" data-testid="pg-error">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{fieldErrors._global}</span>
            </div>
          )}

          {/* Sección 1 — Información básica */}
          <section>
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
              Información básica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                label="Nombre completo"
                htmlFor="nombre_completo"
                required
                testid="pg-nombre"
              >
                <input
                  id="nombre_completo"
                  data-testid="pg-nombre-input"
                  type="text"
                  value={local.nombre_completo}
                  onChange={(e) => setLocal((s) => ({ ...s, nombre_completo: e.target.value }))}
                  onBlur={(e) => commit("nombre_completo", e.target.value.trim())}
                  className="input-field"
                  maxLength={80}
                  minLength={2}
                />
              </FormField>

              <FormField
                label="Correo electrónico"
                htmlFor="correo"
                helper={perfilGlobal.verificado?.correo ? "Verificado" : "Cambia tu correo desde Configuración → Seguridad"}
                verified={perfilGlobal.verificado?.correo}
                testid="pg-correo"
              >
                <input
                  id="correo"
                  data-testid="pg-correo-input"
                  type="email"
                  value={perfilGlobal.correo}
                  readOnly
                  disabled
                  className="input-field bg-slate-50 cursor-not-allowed"
                />
              </FormField>

              <FormField
                label="Teléfono"
                htmlFor="telefono"
                helper={perfilGlobal.verificado?.telefono ? "Verificado" : "Formato: +51 987 654 321"}
                verified={perfilGlobal.verificado?.telefono}
                testid="pg-telefono"
              >
                <input
                  id="telefono"
                  data-testid="pg-telefono-input"
                  type="tel"
                  value={local.telefono}
                  onChange={(e) => setLocal((s) => ({ ...s, telefono: e.target.value }))}
                  onBlur={(e) => commit("telefono", e.target.value.trim())}
                  className="input-field"
                  maxLength={32}
                  placeholder="+51 987 654 321"
                />
              </FormField>

              <FormField
                label="Tipo de usuario"
                htmlFor="tipo_usuario"
                helper="Puedes cambiarlo cuando lo necesites."
                testid="pg-tipo"
              >
                <select
                  id="tipo_usuario"
                  data-testid="pg-tipo-select"
                  value={local.tipo_usuario}
                  onChange={(e) => {
                    setLocal((s) => ({ ...s, tipo_usuario: e.target.value }));
                    commit("tipo_usuario", e.target.value);
                  }}
                  className="input-field"
                >
                  {TIPOS_USUARIO.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </FormField>
            </div>
          </section>

          {/* Sección 2 — Ubicación */}
          <section className="border-t border-slate-100 pt-6">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
              Ubicación
            </h3>
            <UbicacionSelector
              value={local.ubicacion}
              onChange={(u) => {
                setLocal((s) => ({ ...s, ubicacion: u }));
                commitUbicacion(u);
              }}
              testIdPrefix="pg-ubicacion"
            />
          </section>

          {/* Sección 3 — Descripción */}
          <section className="border-t border-slate-100 pt-6">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
              Descripción corta
            </h3>
            <FormField
              label="Cuéntanos sobre ti"
              htmlFor="descripcion_corta"
              helper={`${descLen}/280 · ${descLen >= 40 ? "Excelente, aporta a tu progreso." : "Añade al menos 40 caracteres para completar esta sección."}`}
              testid="pg-descripcion"
            >
              <textarea
                id="descripcion_corta"
                data-testid="pg-descripcion-input"
                value={local.descripcion_corta}
                onChange={(e) => setLocal((s) => ({ ...s, descripcion_corta: e.target.value.slice(0, 280) }))}
                onBlur={(e) => commit("descripcion_corta", e.target.value.trim())}
                className="input-field min-h-[100px] resize-y"
                maxLength={280}
                placeholder="Ej: Especialista en acabados con 8 años de experiencia en Lima. Cuéntanos qué te hace único…"
              />
            </FormField>
          </section>
        </div>
      </div>
    </div>
  );
}

function FormField({ label, htmlFor, helper, required, verified, testid, children }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
        {label}
        {required && <span className="text-rose-600" aria-hidden>*</span>}
        {verified && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            <Check size={11} /> verificado
          </span>
        )}
      </label>
      {children}
      {helper && <p className="text-xs text-slate-500 mt-1.5">{helper}</p>}
    </div>
  );
}
